import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Section } from '@telegram-apps/telegram-ui'

import { api } from '../../lib/api'
import type { Task } from '../../lib/types'
import PageHeader from '../../components/PageHeader'
import TaskCard from '../../components/TaskCard'
import { SkeletonList } from '../../components/Skeleton'
import EmptyState from '../../components/EmptyState'

/** Активные поручения для исполнителя (Анастасии или Владимира). */
export default function AssigneeActive() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<Task[]>({
    queryKey: ['tasks', 'active', 'mine'],
    queryFn: () => api.get<Task[]>('/tasks?status=pending'),
  })

  const markDone = useMutation({
    mutationFn: (taskId: number) => api.post(`/tasks/${taskId}/done`),
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: ['tasks', 'active', 'mine'] })
      const prev = qc.getQueryData<Task[]>(['tasks', 'active', 'mine'])
      qc.setQueryData<Task[]>(['tasks', 'active', 'mine'], (old) => old?.filter((t) => t.id !== taskId) ?? [])
      return { prev }
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(['tasks', 'active', 'mine'], ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['my-calendar'] })
    },
  })

  return (
    <>
      <PageHeader
        title="Поручения"
        caption={data ? `Активных: ${data.length}` : undefined}
      />
      {isLoading ? (
        <SkeletonList count={5} />
      ) : !data || data.length === 0 ? (
        <EmptyState emoji="🌤" title="Всё чисто" description="Активных поручений сейчас нет." />
      ) : (
        <Section>
          {data.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              canCheck={true}
              onToggleDone={(id) => markDone.mutate(id)}
              detailPath={`/tasks/${t.id}`}
            />
          ))}
        </Section>
      )}
    </>
  )
}
